// src/utils/followUpDrafts.js
// Default check-in drafts for the Past Client Follow-Ups program, one per
// touchpoint per side, in Henry's voice. Short enough to text; the same body
// is used for email (wrapped in the branded template by the page).
// STYLE RULE: no em dashes anywhere in client-facing copy.
import { interpolate } from "./emailPlaceholders";
import { touchLabel, buildTouches } from "./followUpPlan";

// The program's touch keys, derived from the schedule engine so a new
// touchpoint can never silently lack copy (the date argument is arbitrary).
export const TOUCH_KEYS = Object.keys(buildTouches("2000-01-15"));

const D = {
  "month-1": {
    buyer: {
      subject: "How is the new place treating you?",
      body:
        "Hi {{clientFirstNames}}! It has been a month since you got the keys to {{propertyAddress}}. " +
        "How is the new place treating you? If anything has come up that you need a good contractor " +
        "or vendor for, I have a list of people we trust. Happy to help with anything at all.",
    },
    seller: {
      subject: "Checking in after your sale",
      body:
        "Hi {{clientFirstNames}}! It has been about a month since we closed on {{propertyAddress}}. " +
        "I hope the move has gone smoothly. If there is anything you still need a hand with, or " +
        "paperwork you cannot find, just say the word. I am always here.",
    },
  },
  "month-2": {
    buyer: {
      subject: "Settling in okay?",
      body:
        "Hi {{clientFirstNames}}, just thinking about you. Feeling settled at {{propertyAddress}} yet? " +
        "No agenda here, I just like to keep up with how my favorite people are doing. Anything you need, I am a text away.",
    },
    seller: {
      subject: "Quick hello",
      body:
        "Hi {{clientFirstNames}}, just checking in. I hope life after the sale has been treating you well. " +
        "If any mail for the old house is still chasing you or you need anything at all, let me know.",
    },
  },
  "month-3": {
    buyer: {
      subject: "Three months in the new home",
      body:
        "Hi {{clientFirstNames}}! Hard to believe it has been three months at {{propertyAddress}}. " +
        "How is everything holding up? If you have a minute sometime, I would love to hear how it is going. " +
        "And if we did right by you, a short review means the world to a small team like ours.",
    },
    seller: {
      subject: "Three months since closing",
      body:
        "Hi {{clientFirstNames}}! Three months since we closed on {{propertyAddress}}. I hope this chapter " +
        "has been good to you. If we did right by you, a short review means the world to a small team like ours. " +
        "Either way, it is always good to hear from you.",
    },
  },
  "month-6": {
    buyer: {
      subject: "Half a year already",
      body:
        "Hi {{clientFirstNames}}! Six months at {{propertyAddress}} already. With the seasons changing, " +
        "if you need a furnace check, gutter cleaning, or any seasonal help, I have trusted people for all of it. " +
        "Hope you are doing great.",
    },
    seller: {
      subject: "Six months later",
      body:
        "Hi {{clientFirstNames}}! Six months since your sale closed. I hope everything since has gone the way " +
        "you hoped. If you or anyone you know ever needs real estate help, you know where to find me.",
    },
  },
  "month-9": {
    buyer: {
      subject: "Your home is working for you",
      body:
        "Hi {{clientFirstNames}}! Nine months into {{propertyAddress}} and homes around you have been moving. " +
        "If you are ever curious what yours is worth now, I can put together a quick value update, no strings attached. " +
        "Hope all is well!",
    },
    seller: {
      subject: "A quick market note",
      body:
        "Hi {{clientFirstNames}}! The market has kept moving since we closed and I thought of you. " +
        "If you are ever curious what is happening with values in the neighborhood, I am happy to share a quick update. " +
        "Hope all is well!",
    },
  },
  anniversary: {
    buyer: {
      subject: "Happy home anniversary!",
      body:
        "Happy home anniversary, {{clientFirstNames}}! {{years}} at {{propertyAddress}}. " +
        "I still remember handing you the keys. I hope the home has been everything you wanted. " +
        "If you are ever curious what it is worth today, I would be glad to run the numbers for you. Hope you get to celebrate.",
    },
    seller: {
      subject: "Thinking of you today",
      body:
        "Hi {{clientFirstNames}}! It has been {{years}} since we closed on {{propertyAddress}} and " +
        "I always remember my favorite closings. I hope everything since has been wonderful. If you or " +
        "someone you care about ever needs a hand with real estate, I am always in your corner.",
    },
  },
};

export function followUpDraft(touchKey, side, ctx = {}) {
  const key = String(touchKey || "");
  // A default param only fires on `undefined`; an explicit `null` (which
  // callers can genuinely pass) would otherwise reach `ctx.clientFirstNames`
  // below and throw.
  ctx = ctx || {};
  const group = key.startsWith("anniversary-") ? D.anniversary : D[key];
  // Strict side lookup: a missing seller draft must fail visibly, never
  // silently serve buyer copy to a seller (own-property check so weird side
  // values can't reach Object.prototype).
  const t =
    group && Object.prototype.hasOwnProperty.call(group, side)
      ? group[side]
      : null;
  if (!t) return { subject: touchLabel(key), body: "" };
  const n = key.startsWith("anniversary-") ? Number(key.split("-")[1]) : 0;
  // Defaults keep a legacy record from rendering "Hi !" or "keys to .",
  // years derives from the key so callers can't get it wrong, and the final
  // sweep strips any token that still survived: raw {{placeholders}} must
  // never reach a client's phone.
  const data = {
    clientFirstNames: String(ctx.clientFirstNames || "").trim() || "there",
    propertyAddress: String(ctx.propertyAddress || "").trim() || "the property",
    years: n === 1 ? "1 year" : n > 1 ? `${n} years` : "",
  };
  const fill = (s) =>
    interpolate(s, data)
      .replace(/\{\{[^}]+\}\}/g, "")
      // Collapse only runs of horizontal whitespace, not all whitespace, so
      // a future multi-line draft body keeps its intentional line breaks.
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  return { subject: fill(t.subject), body: fill(t.body) };
}
